/**
 * Step definitions for the onboarding tour.
 *
 * A step is one of:
 *  - { type: 'modal', ... }      centered card (welcome / completion screens)
 *  - { type: 'spotlight', ... }  anchored tooltip highlighting a target element
 *
 * Spotlight steps with `requiresMenu: true` target menu items; the tour opens
 * the menu before showing them. Steps whose target never appears are skipped
 * automatically, so the tour degrades gracefully if menu items are gated off
 * for this user (allowedPages).
 *
 * Steps with `interactive: true` let the user click the highlighted element
 * itself (the click shield gets a cut-out over the target) — the tour advances
 * automatically when the step's `advanceOnRoute` is reached, or via Next.
 *
 * The family steps come FIRST: a user without a family sees no menu items
 * (only Home), so setting up or joining a family must happen before the
 * menu-driven steps can work.
 */

const steps = [
  {
    type: 'modal',
    id: 'welcome',
    title: 'Welcome to Money Lens!',
    text: 'A quick tour of the essentials — it takes about a minute. You can skip anytime and replay it later from your account menu.',
    nextLabel: 'Start tour',
  },
  {
    type: 'spotlight',
    id: 'open-account-panel',
    route: null,
    target: '[data-tour="open-account-panel"]',
    interactive: true,
    title: 'Your account & family',
    text: 'Click the person icon (top-right) to open your account panel.',
  },
  {
    type: 'spotlight',
    id: 'family-management',
    route: null,
    target: '[data-tour="family-management"]',
    advanceOnRoute: '/family',
    interactive: true,
    title: 'Family Management',
    text: 'Click "Family Management" to set up your family — you need one before the rest of the app unlocks.',
  },
  {
    type: 'spotlight',
    id: 'family-page',
    route: '/family',
    target: '[data-tour="create-family"], [data-tour="create-family-cta"], [data-tour="family-name"]',
    title: 'Create or join a family',
    text: 'Create a family and invite members by email, or join one with an invitation link. Sharing a family lets you see each other\'s accounts and transactions. Once it\'s set up, the page reloads and the full menu appears.',
  },
  {
    type: 'spotlight',
    id: 'menu-manage-accounts',
    route: null,
    target: '[data-tour="menu-manage-accounts"]',
    requiresMenu: true,
    advanceOnRoute: '/manage-accounts',
    interactive: true,
    title: 'First: create an account',
    text: 'Open the menu and pick "Manage Accounts" — this is where you register a bank or cash account.',
  },
  {
    type: 'spotlight',
    id: 'new-account',
    route: '/manage-accounts',
    target: '[data-tour="new-account"]',
    interactive: true,
    title: 'Create your account',
    text: 'Click "New Account" to open the account form.',
  },
  {
    type: 'spotlight',
    id: 'account-panel',
    route: '/manage-accounts',
    target: '[data-tour="account-form-panel"]',
    title: 'The account form',
    text: 'Fill in a name, pick the type and currency, set an initial balance, then save. Your accounts will show up in every dropdown afterwards.',
  },
  {
    type: 'spotlight',
    id: 'menu-transactions',
    route: null,
    target: '[data-tour="menu-transactions"]',
    requiresMenu: true,
    advanceOnRoute: '/transactions',
    interactive: true,
    title: 'Next: record a transaction',
    text: 'Click "Transactions" in the menu — that is where all your entries live.',
  },
  {
    type: 'spotlight',
    id: 'new-transaction',
    route: '/transactions',
    target: '[data-tour="new-transaction"]',
    interactive: true,
    title: 'New Transaction',
    text: 'Click "New Transaction" — the form opens in a side panel.',
  },
  {
    type: 'spotlight',
    id: 'type-expense',
    route: null,
    target: '[data-tour="type-expense"]',
    title: 'Expenses',
    text: 'Pick "Expense" to record something you spent.',
  },
  {
    type: 'spotlight',
    id: 'type-income',
    route: null,
    target: '[data-tour="type-income"]',
    title: 'Income',
    text: 'Pick "Income" for money you receive, like a salary.',
  },
  {
    type: 'spotlight',
    id: 'type-transfer',
    route: null,
    target: '[data-tour="type-transfer"]',
    title: 'Transfers between accounts',
    text: '"Transfer" moves money between two of your own accounts.',
  },
  {
    type: 'spotlight',
    id: 'menu-manage-recurrences',
    route: null,
    target: '[data-tour="menu-manage-recurrences"]',
    requiresMenu: true,
    advanceOnRoute: '/manage-recurrences',
    interactive: true,
    title: 'Automate with recurrences',
    text: 'Click "Manage Recurrences" — recurring transactions (rent, salary, subscriptions) are managed here.',
  },
  {
    type: 'spotlight',
    id: 'create-recurrence',
    route: '/manage-recurrences',
    target: '[data-tour="create-recurrence"]',
    title: 'Create Recurrence',
    text: 'Click "Create Recurrence" to schedule a transaction that repeats automatically.',
  },
  {
    type: 'spotlight',
    id: 'menu-manage-subjects',
    route: null,
    target: '[data-tour="menu-manage-subjects"]',
    requiresMenu: true,
    advanceOnRoute: '/manage-subjects',
    interactive: true,
    title: 'Organise: subjects & categories',
    text: 'Click "Manage Subjects" — subjects are who you transact with (Supermarket, Employer…). Categories to group your spending live right next to it in the menu.',
  },
  {
    type: 'modal',
    id: 'done',
    title: "You're all set!",
    text: "That's the essentials. Replay this tour anytime from the account menu (top-right).",
  },
];

export const TOUR_STEPS = steps;